/**
 * App
 * ---
 * Top-level shell. Wires up:
 *   - TanStack Query for server-state caching
 *   - Tooltip + Toast providers (shadcn)
 *   - React Router routes (public + auth-protected, lazy-loaded)
 *   - ErrorBoundary so a crash in any single route won't blank the app
 *   - Suspense fallback for code-split route bundles
 */
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import RequireAuth from "./components/RequireAuth";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Skeleton } from "@/components/ui/skeleton";

// Route-level code splitting: each page becomes its own JS chunk so the
// initial landing-page bundle stays small. Vite emits one chunk per import().
const Home = lazy(() => import("./pages/Home"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ScenarioBuilder = lazy(() => import("./pages/ScenarioBuilder"));
const ScenarioDetail = lazy(() => import("./pages/ScenarioDetail"));
const PersonaBuilder = lazy(() => import("./pages/PersonaBuilder"));
const PrepWizard = lazy(() => import("./pages/PrepWizard"));
const Simulation = lazy(() => import("./pages/Simulation"));
const Debrief = lazy(() => import("./pages/Debrief"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const BaselineChallenge = lazy(() => import("./pages/BaselineChallenge"));
const VoiceLab = lazy(() => import("./pages/VoiceLab"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Cookies = lazy(() => import("./pages/legal/Cookies"));
const Subprocessors = lazy(() => import("./pages/legal/Subprocessors"));
const SecurityPage = lazy(() => import("./pages/legal/Security"));
const DPA = lazy(() => import("./pages/legal/DPA"));
const PrivacySettings = lazy(() => import("./pages/settings/Privacy"));
const SecuritySettings = lazy(() => import("./pages/settings/Security"));
const AdminConsole = lazy(() => import("./pages/admin/AdminConsole"));
const OrgSettings = lazy(() => import("./pages/settings/OrgSettings"));
const ManagerDashboard = lazy(() => import("./pages/ManagerDashboard"));
import { ConsentBanner } from "./components/legal/ConsentBanner";

// Single shared QueryClient instance — defined at module scope so route
// remounts don't blow away the cache.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Most data here changes on user action, not in the background. A short
      // stale time avoids redundant refetches but keeps the UI responsive.
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Minimal route fallback shown while a lazy-loaded page chunk is downloading.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center p-6">
    <div className="w-full max-w-md space-y-4">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full" />
    </div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/baseline" element={<RequireAuth><BaselineChallenge /></RequireAuth>} />
              <Route path="/voice-lab" element={<RequireAuth><VoiceLab /></RequireAuth>} />
              <Route path="/knowledge" element={<RequireAuth><KnowledgeBase /></RequireAuth>} />
              <Route path="/scenario/new" element={<RequireAuth><ScenarioBuilder /></RequireAuth>} />
              <Route path="/scenario/:id" element={<RequireAuth><ScenarioDetail /></RequireAuth>} />
              <Route path="/scenario/:id/edit" element={<RequireAuth><ScenarioBuilder /></RequireAuth>} />
              <Route path="/scenario/:id/persona" element={<RequireAuth><PersonaBuilder /></RequireAuth>} />
              <Route path="/scenario/:id/prep" element={<RequireAuth><PrepWizard /></RequireAuth>} />
              <Route path="/scenario/:id/simulate" element={<RequireAuth><Simulation /></RequireAuth>} />
              <Route path="/scenario/:id/debrief/:sessionId" element={<RequireAuth><Debrief /></RequireAuth>} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/subprocessors" element={<Subprocessors />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/dpa" element={<DPA />} />
              <Route path="/settings/privacy" element={<RequireAuth><PrivacySettings /></RequireAuth>} />
              <Route path="/settings/security" element={<RequireAuth><SecuritySettings /></RequireAuth>} />
              <Route path="/admin" element={<RequireAuth><AdminConsole /></RequireAuth>} />
              <Route path="/settings/organization" element={<RequireAuth><OrgSettings /></RequireAuth>} />
              <Route path="/manager" element={<RequireAuth><ManagerDashboard /></RequireAuth>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ConsentBanner />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
